import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { WorkGroupT } from '@/pages/workgroup/type';
import { useQueryClient } from 'react-query';
import { WorkspaceSelector } from '@/components/graphEditor/WorkspaceSelector';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { getGrafanaURL } from '@/utils/utils';
import { memo, useEffect, useState } from 'react';
import { LoadingIndicator } from '@/components/loading-indicator';
import { useOrgContext } from '@/contexts/orgContext';
import { calculateRoleForSpace } from '@/pages/admin/user/type';
import NoPermission from '@/components/NoPermission';
import { TableContainer } from '@/lib/styled';
import EmptyState from '@/pages/workgroup/EmptyState';

const GrafanaIframe = memo(({ grafanaURL }: { grafanaURL: string }) => {
  const [css] = useStyletron();
  return <iframe title="monitor" src={grafanaURL} className={css({ width: '100%', height: '100%' })} />;
});

export function Monitor({ group }: { group: WorkGroupT }) {
  const [css] = useStyletron();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspaceContext();
  const { userInfo } = useOrgContext();

  const [showLoading, setShowLoading] = useState(false);

  const grafanaURL = getGrafanaURL(group.org_id, currentWorkspace.workspace_id, currentWorkspace.workgroup_id);

  useEffect(() => {
    if (currentWorkspace.workspace_id) {
      setShowLoading(true);
    }

    const handleIframeMessage = (event: MessageEvent<{ type: string }>) => {
      if (event.data.type === 'iframe-ready') {
        setShowLoading(false);
      }
    };
    window.addEventListener('message', handleIframeMessage, false);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [currentWorkspace.workspace_id]);

  const userRoleForSpace = calculateRoleForSpace(userInfo.roles, group.workgroup_id, currentWorkspace.workspace_id);
  const hasPermission = userRoleForSpace === 'workspace-users' || userRoleForSpace === 'workspace-admins';

  return (
    <>
      {group.workspaces.length == 0 && (
        <TableContainer
          className={css({
            height: 'calc(100vh - 96px)',
          })}
        >
          <EmptyState createWorkGroup={false} />
        </TableContainer>
      )}
      {group.workspaces.length > 0 && (
        <div
          className={css({
            height: 'calc(100vh - 89px)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          })}
        >
          <div className={css({ marginBottom: '16px' })}>
            <WorkspaceSelector workgroupId={group.workgroup_id} showPending />
          </div>
          <div className={css({ width: '100%', flexGrow: 1, position: 'relative' })}>
            {!hasPermission ? (
              <NoPermission />
            ) : (
              <>
                {showLoading && (
                  <div
                    className={css({
                      position: 'absolute',
                      background: 'rgb(248, 248, 248)',
                      width: '100%',
                      height: '100%',
                    })}
                  >
                    <LoadingIndicator />
                  </div>
                )}
                {currentWorkspace && <GrafanaIframe grafanaURL={grafanaURL} />}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
